import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import routes from 'routes';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import ParticipatingClubsSummary from 'components/admin/ParticipatingClubsSummary';
import CreatableClubSelect, {
  UNATTACHED,
} from 'components/admin/MeetRegistration/AddEditRegistrationPopup/CreatableClubSelect';
import {
  getMyMeetRegistrations,
  selfRegisterAthlete,
  selfWithdrawAthlete,
  getAthleteAvailableEvents,
} from 'api/meetRegistrationApi';

import competitionMeetImage from 'assets/images/uicc.png';
import styles from './MeetRegistrationManager.module.css';

// The shared "My Registrations" MANAGEMENT view (§6.10) — the dashboard a user with an account uses to manage their own
// athletes' registrations for one meet: register, edit events, withdraw, see who's in. It is deliberately chrome-less so
// it can be hosted in whichever shell matches the user:
//   • known to the host club  → wrapped in the club app (AdminLayout) — MyMeetRegistrations.
//   • clubless / other club   → wrapped in the personal portal (PersonalLayout) — MyMeetSignup.
// (A brand-new, never-onboarded person does NOT use this — they get the guided onboarding wizard instead.)
// Self-contained: it loads its own data for `meetId` and owns its modals. "Add an athlete" sends the user to their
// profile (the create-athlete surface, the same on either shell).

// Meet date shown as stored local wall-clock (the "Olympics model" — an ISO string without 'Z' parses as local), mirroring
// the public landing page so the banner reads identically.
const fmtMeetDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
};
const displayAge = (a) => {
  const v = a.ageGroup != null ? a.ageGroup : ageFromDob(a.birthDate);
  return v != null ? v : '—';
};

const membershipLabel = (t) => (t === 'Full' ? 'Member' : t || '');

const ageGenderLabel = (reg) =>
  [reg.ageGroup != null ? String(reg.ageGroup) : '', reg.athlete?.gender || '']
    .filter(Boolean)
    .join(' ');

const eventsNoteText = (reg) => {
  const ctx = ageGenderLabel(reg);
  if (reg.maxEvents != null) {
    return `${ctx ? `${ctx} athletes` : 'These athletes'} can register for up to ${reg.maxEvents} event${
      reg.maxEvents === 1 ? '' : 's'
    }.`;
  }
  return ctx ? `Events available for ${ctx} athletes.` : 'Events available for this athlete.';
};

const noEventsText = (reg) => {
  const ctx = ageGenderLabel(reg);
  return `This meet's program doesn't include any events for ${
    ctx ? `${ctx} athletes` : 'this athlete'
  }. If you think this is a mistake, please contact a club official.`;
};

const MeetRegistrationManager = ({ meetId, addAthleteProfilePath = routes.personal.profile }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState(null);

  const [reg, setReg] = useState(null); // register/edit modal
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [showAddInfo, setShowAddInfo] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getMyMeetRegistrations(meetId);
      setData(res.data);
      setLoadError(false);
    } catch (err) {
      toast.error(err.response?.data || 'Failed to load registrations');
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [meetId]);

  useEffect(() => {
    load();
  }, [load]);

  const openRegister = async (athlete) => {
    const initialSelected = athlete.events?.map((e) => e.eventCode) || [];
    // Pre-fill the representing club: their explicit club if set; else if they're already registered with none, they
    // were deliberately UNATTACHED (so pre-select that, not blank — otherwise editing would force a re-choice); else
    // for a brand-new not-in-club athlete, a club they belong to elsewhere; else blank (must choose).
    const initialExternalClub =
      athlete.representingClub ||
      (athlete.isRegistered
        ? UNATTACHED
        : !athlete.isInThisClub
          ? athlete.currentClubs?.[0] || ''
          : '');
    setReg({
      athlete,
      events: [],
      selected: [...initialSelected],
      maxEvents: null,
      ageGroup: null,
      bib: '',
      noBib: false,
      externalClub: initialExternalClub,
      // Snapshot of the opening state, so an unchanged edit can skip the API call.
      initialSelected,
      initialExternalClub,
      loadingEvents: true,
      saving: false,
    });
    try {
      const res = await getAthleteAvailableEvents(meetId, {
        birthDate: athlete.birthDate,
        gender: athlete.gender,
      });
      setReg((prev) =>
        prev && prev.athlete.athleteProfileId === athlete.athleteProfileId
          ? {
              ...prev,
              events: res.data.events || [],
              maxEvents: res.data.maxEvents ?? null,
              ageGroup: res.data.ageGroup ?? null,
              loadingEvents: false,
            }
          : prev
      );
    } catch (err) {
      toast.error(err.response?.data || 'Failed to load events');
      setReg((prev) => (prev ? { ...prev, loadingEvents: false } : prev));
    }
  };

  const toggleEvent = (code) => {
    setReg((prev) => {
      if (!prev) return prev;
      const has = prev.selected.includes(code);
      if (!has && prev.maxEvents != null && prev.selected.length >= prev.maxEvents) return prev;
      return {
        ...prev,
        selected: has ? prev.selected.filter((c) => c !== code) : [...prev.selected, code],
      };
    });
  };

  const saveRegister = async () => {
    if (!reg) return;
    if (reg.selected.length === 0) {
      toast.error('Select at least one event');
      return;
    }
    const notInClub = !reg.athlete.isInThisClub;
    const fullMemberHere = reg.athlete.isInThisClub && reg.athlete.membershipType === 'Full';
    if (notInClub && !reg.noBib && !(Number(reg.bib) > 0)) {
      toast.error('Enter a bib number, or choose "No number — the club will assign one"');
      return;
    }
    // The representing-club field is shown for everyone except a full member of the host club; when it's shown they must
    // make an explicit choice (a club, or "No club / unattached") — never leave it blank.
    if (!fullMemberHere && !reg.externalClub) {
      toast.error('Choose the club they represent, or select "No club / unattached"');
      return;
    }
    // Editing an existing registration with nothing changed (same events + representing club) → close without an API call.
    if (reg.athlete.isRegistered) {
      const cur = [...reg.selected].sort((a, b) => a - b);
      const orig = [...reg.initialSelected].sort((a, b) => a - b);
      const sameEvents = cur.length === orig.length && cur.every((c, i) => c === orig[i]);
      if (sameEvents && reg.externalClub === reg.initialExternalClub) {
        toast.info('No changes to save');
        setReg(null);
        return;
      }
    }
    setReg((prev) => ({ ...prev, saving: true }));
    try {
      const payload = { athleteProfileId: reg.athlete.athleteProfileId, eventCodes: reg.selected };
      if (notInClub) {
        payload.noBib = reg.noBib;
        payload.bib = reg.noBib ? null : Number(reg.bib);
      }
      if (!fullMemberHere) {
        payload.updateRepresentingClub = true;
        payload.externalClubName =
          reg.externalClub === UNATTACHED ? null : reg.externalClub.trim() || null;
      }
      await selfRegisterAthlete(meetId, payload);
      toast.success('Registration saved');
      setReg(null);
      load();
    } catch (err) {
      toast.error(err.response?.data || 'Failed to save registration');
      setReg((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  };

  const confirmWithdraw = async () => {
    const a = withdrawTarget;
    setWithdrawTarget(null);
    if (!a) return;
    try {
      await selfWithdrawAthlete(meetId, a.athleteProfileId);
      toast.success('Withdrawn from the meet');
      load();
    } catch (err) {
      toast.error(err.response?.data || 'Failed to withdraw');
    }
  };

  // The status box also tells the user what this page is FOR — a bold lead + a window-state detail line. Tone + copy
  // adapt to the window state (open / not-yet-open / closed / full).
  const renderBanner = () => {
    if (!data) return null;
    let tone = styles.bannerOpen;
    let title = 'Register your athletes for this meet';
    let detail = `Add each of your athletes below and choose their events.${
      data.signupCloseAt ? ` Registration is open until ${fmtMeetDate(data.signupCloseAt)}.` : ''
    }`;
    if (data.windowState === 'BeforeOpen') {
      tone = styles.bannerWarn;
      title = 'Registration hasn’t opened yet';
      detail = `This is where you’ll register your athletes for this meet. Registration opens ${fmtMeetDate(
        data.signupOpenAt
      )}.`;
    } else if (data.windowState === 'Closed') {
      tone = styles.bannerClosed;
      title = 'Registration is closed';
      detail = 'Registration for this meet has closed. You can still review your athletes’ entries below.';
    } else if (data.isFull) {
      tone = styles.bannerWarn;
      title = 'Registration is full';
      detail = 'This meet has reached its limit. You can still edit or withdraw your existing entries below.';
    }
    return (
      <div className={`${styles.banner} ${tone}`}>
        <div className={styles.bannerTitle}>{title}</div>
        <div className={styles.bannerDetail}>{detail}</div>
      </div>
    );
  };

  const renderStatus = (a) => {
    if (a.isInThisClub && a.membershipType) {
      const label = membershipLabel(a.membershipType);
      const cls =
        label === 'Member'
          ? styles.badgeMember
          : label === 'Guest'
            ? styles.badgeGuest
            : label === 'Visitor'
              ? styles.badgeVisitor
              : styles.badgeNeutral;
      return <span className={`${styles.badge} ${cls}`}>{label}</span>;
    }
    return <span className={`${styles.badge} ${styles.badgeNeutral}`}>Not a member</span>;
  };

  const renderClub = (a) => {
    // A full member of the host club represents the host club by default. A guest/visitor with no representing club is
    // deliberately UNATTACHED (the snapshot stores null) — don't fall back to the host club, show "—" instead.
    const name =
      a.representingClub ||
      (a.isInThisClub
        ? a.membershipType === 'Full'
          ? data?.hostClubName || ''
          : ''
        : a.currentClubs && a.currentClubs.length > 0
          ? a.currentClubs.join(', ')
          : '');
    return name ? (
      <span className={styles.clubCell}>{name}</span>
    ) : (
      <span className={styles.muted}>—</span>
    );
  };

  const renderEvents = (a) => {
    if (!a.events || a.events.length === 0) return <span className={styles.muted}>—</span>;
    return (
      <div className={styles.eventTags}>
        {a.events.map((e) => (
          <span key={e.eventCode} className={styles.eventTag}>
            {e.displayName}
          </span>
        ))}
      </div>
    );
  };

  if (loading) return <div className={styles.empty}>Loading…</div>;
  if (loadError)
    return (
      <div className={styles.noticeCard}>
        <h3 className={styles.noticeTitle}>Couldn’t load your registrations</h3>
        <p className={styles.noticeBody}>Please refresh and try again.</p>
      </div>
    );

  const athletes = data?.athletes || [];
  const registered = athletes.filter((a) => a.isRegistered);
  const notRegistered = athletes.filter((a) => !a.isRegistered);
  const selfServiceEnabled = data?.selfServiceEnabled;

  const clubs = (data?.participatingClubs || []).map((c) => ({
    name: c.clubName || 'Unattached',
    count: c.athleteCount,
    isHost:
      !!data?.hostClubName &&
      (c.clubName || '').trim().toLowerCase() === data.hostClubName.trim().toLowerCase(),
  }));
  const totalAthletes = clubs.reduce((sum, c) => sum + (c.count || 0), 0);

  const modalClubOptions = (() => {
    if (!reg) return [];
    const host = (data?.hostClubName || '').trim().toLowerCase();
    const byKey = new Map();
    const add = (raw) => {
      const n = (raw || '').trim();
      if (!n) return;
      const k = n.toLowerCase();
      if (k === host || byKey.has(k)) return;
      byKey.set(k, n);
    };
    (data?.participatingClubs || []).forEach((c) => add(c.clubName));
    (reg.athlete.currentClubs || []).forEach(add);
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
  })();

  const isFullMemberHere =
    !!reg && reg.athlete.isInThisClub && reg.athlete.membershipType === 'Full';
  const showBibField = !!reg && !reg.athlete.isInThisClub;
  const showRepresentingClub = !!reg && !isFullMemberHere;

  // "Meet Details" target — once they're in the host club → the in-club meet details page; otherwise → the public landing
  // page. The label is destination-based ("Meet Details") so it reads the same however they arrived.
  const hostClubId = data?.clubID || data?.clubId;
  const goToMeetDetails = () => {
    if (data?.callerInHostClub && hostClubId) navigate(routes.admin.showMeet.url(hostClubId, meetId));
    else if (data?.signupCode) navigate(routes.public.meetSignup.url(data.signupCode));
  };
  const canShowMeetDetails = !!((data?.callerInHostClub && hostClubId) || data?.signupCode);

  return (
    <>
      {/* Compact meet hero — echoes the public landing page (same image + dark overlay, host-club kicker, meet title,
          date · location) so a registrant arriving from there feels continuity instead of dropping into an admin view. */}
      <div
        className={styles.meetHero}
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.78), rgba(15,23,42,0.92)), url(${competitionMeetImage})`,
        }}
      >
        <div className={styles.meetHeroInner}>
          <div className={styles.meetHeroText}>
            {data?.hostClubName && <div className={styles.meetHeroKicker}>{data.hostClubName}</div>}
            <h1 className={styles.meetHeroTitle}>{data?.meetName}</h1>
            <div className={styles.meetHeroMeta}>
              {fmtMeetDate(data?.meetDate)}
              {data?.location ? ` · ${data.location}` : ''}
            </div>
          </div>
          {canShowMeetDetails && (
            <CustomNewButton variant="outline" onClick={goToMeetDetails}>
              Meet Details
            </CustomNewButton>
          )}
        </div>
      </div>

      {/* Status box first — it both tells the user what this page is for and shows the registration window. */}
      {selfServiceEnabled && renderBanner()}

      {clubs.length > 0 && (
        <div className={styles.clubsCard}>
          <ParticipatingClubsSummary
            clubs={clubs}
            totalClubs={clubs.length}
            totalAthletes={totalAthletes}
          />
        </div>
      )}

      {!selfServiceEnabled ? (
        <div className={styles.noticeCard}>
          <h3 className={styles.noticeTitle}>Registration is managed by the club</h3>
          <p className={styles.noticeBody}>
            Online self-service registration isn&apos;t available for this meet. To register an
            athlete, please contact a club official and they&apos;ll add them for you.
          </p>
        </div>
      ) : (
        <>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>My Athletes</h3>
            <CustomNewButton variant="primary2" onClick={() => setShowAddInfo(true)}>
              Add an athlete
            </CustomNewButton>
          </div>

          {athletes.length === 0 ? (
            <div className={styles.athletesCard}>
              <div className={styles.empty}>
                You have no athletes yet. Add one on your profile, then come back here to register
                them for this meet.
              </div>
            </div>
          ) : (
            <>
              {registered.length > 0 && (
                <div className={styles.tableCard}>
                  <div className={styles.tableCardHeader}>
                    <h4 className={styles.tableCardTitle}>Registered</h4>
                    <span className={styles.tableCardCount}>{registered.length}</span>
                  </div>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th className={styles.nowrap}>Age · Gender</th>
                          <th>Status</th>
                          <th>Club</th>
                          <th>Bib</th>
                          <th>Events</th>
                          <th className={styles.actionsCol}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registered.map((a) => (
                          <tr key={a.athleteProfileId}>
                            <td>
                              {a.firstName} {a.lastName}
                            </td>
                            <td className={styles.nowrap}>
                              {displayAge(a)} · {a.gender}
                            </td>
                            <td>{renderStatus(a)}</td>
                            <td>{renderClub(a)}</td>
                            <td>{a.bib || '—'}</td>
                            <td>{renderEvents(a)}</td>
                            <td>
                              <div className={styles.actions}>
                                <button
                                  type="button"
                                  className={styles.actionBtn}
                                  title="Edit events"
                                  disabled={!data.canEditExisting}
                                  onClick={() => openRegister(a)}
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.actionBtn} ${styles.deleteAction}`}
                                  title="Withdraw from meet"
                                  disabled={!data.canEditExisting}
                                  onClick={() => setWithdrawTarget(a)}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {notRegistered.length > 0 && (
                <div className={styles.tableCard}>
                  <div className={styles.tableCardHeader}>
                    <h4 className={styles.tableCardTitle}>Not registered</h4>
                    <span className={styles.tableCardCount}>{notRegistered.length}</span>
                  </div>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th className={styles.nowrap}>Age · Gender</th>
                          <th>Status</th>
                          <th>Club</th>
                          <th className={styles.actionsCol}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notRegistered.map((a) => (
                          <tr key={a.athleteProfileId}>
                            <td>
                              {a.firstName} {a.lastName}
                            </td>
                            <td className={styles.nowrap}>
                              {displayAge(a)} · {a.gender}
                            </td>
                            <td>{renderStatus(a)}</td>
                            <td>{renderClub(a)}</td>
                            <td>
                              <div className={styles.actions}>
                                <button
                                  type="button"
                                  className={styles.registerBtn}
                                  disabled={!data.canAddNew}
                                  onClick={() => openRegister(a)}
                                >
                                  Register
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Register / edit-events modal */}
      {reg && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>
              {reg.athlete.isRegistered ? 'Edit events' : 'Register'} — {reg.athlete.firstName}{' '}
              {reg.athlete.lastName}
            </h3>

            {(showBibField || showRepresentingClub) && (
              <div className={styles.mintBox}>
                {showBibField && (
                  <>
                    <label className={styles.fieldLabel}>Bib number</label>
                    <div className={styles.bibRow}>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={styles.bibInput}
                        value={reg.bib}
                        disabled={reg.noBib}
                        onChange={(e) =>
                          setReg((p) => ({ ...p, bib: e.target.value.replace(/[^0-9]/g, '') }))
                        }
                      />
                      <label className={styles.checkLabel}>
                        <input
                          type="checkbox"
                          checked={reg.noBib}
                          onChange={(e) => setReg((p) => ({ ...p, noBib: e.target.checked }))}
                        />
                        No number — the club will assign one
                      </label>
                    </div>
                  </>
                )}
                {showRepresentingClub && (
                  <>
                    <label className={styles.fieldLabel}>Representing club</label>
                    <CreatableClubSelect
                      clubs={modalClubOptions}
                      value={reg.externalClub}
                      onChange={(v) => setReg((p) => ({ ...p, externalClub: v }))}
                      unattachedLabel="No club / unattached"
                      placeholder="Select a club…"
                    />
                  </>
                )}
              </div>
            )}

            <div className={styles.eventsPickHeader}>
              <span className={styles.fieldLabel}>Events</span>
              <span className={styles.countHint}>
                {reg.selected.length}
                {reg.maxEvents != null ? ` / ${reg.maxEvents}` : ''} selected
              </span>
            </div>
            {!reg.loadingEvents && (reg.ageGroup != null || reg.maxEvents != null) && (
              <p className={styles.eventsNote}>{eventsNoteText(reg)}</p>
            )}
            {reg.loadingEvents ? (
              <div className={styles.empty}>Loading events…</div>
            ) : reg.events.length === 0 ? (
              <div className={styles.empty}>{noEventsText(reg)}</div>
            ) : (
              <div className={styles.eventsGrid}>
                {reg.events.map((e) => {
                  const checked = reg.selected.includes(e.eventCode);
                  const capReached = reg.maxEvents != null && reg.selected.length >= reg.maxEvents;
                  return (
                    <label key={e.eventCode} className={styles.eventOption}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!checked && capReached}
                        onChange={() => toggleEvent(e.eventCode)}
                      />
                      {e.displayName}
                    </label>
                  );
                })}
              </div>
            )}

            <div className={styles.modalActions}>
              <CustomNewButton variant="secondary" onClick={() => setReg(null)} disabled={reg.saving}>
                Cancel
              </CustomNewButton>
              <CustomNewButton
                variant="success"
                onClick={saveRegister}
                disabled={reg.saving || reg.loadingEvents}
              >
                {reg.saving ? 'Saving…' : 'Save'}
              </CustomNewButton>
            </div>
          </div>
        </div>
      )}

      {/* "Add an athlete" goes to the profile to CREATE a new athlete — distinct from registering an existing one for
          this meet. Explain that before navigating away so it isn't a surprising jump. */}
      <CustomNewConfirm
        isOpen={showAddInfo}
        onClose={() => setShowAddInfo(false)}
        onCancel={() => setShowAddInfo(false)}
        onConfirm={() => {
          setShowAddInfo(false);
          navigate(addAthleteProfilePath);
        }}
        title="Add a new athlete to your profile?"
        description={
          'This takes you to your profile to create a brand-new athlete on your account ' +
          '(for example, your child). It does NOT register anyone for this meet. Once the athlete ' +
          'exists on your profile, come back here and use the Register button to enter them in this meet.'
        }
        confirmButtonText="Go to my profile"
        confirmButtonVariant="primary2"
        cancelButtonText="Stay here"
      />

      <CustomNewConfirm
        isOpen={!!withdrawTarget}
        onClose={() => setWithdrawTarget(null)}
        onCancel={() => setWithdrawTarget(null)}
        onConfirm={confirmWithdraw}
        title="Withdraw from meet?"
        description={
          withdrawTarget
            ? `Remove ${withdrawTarget.firstName} ${withdrawTarget.lastName} from this meet? This deletes their registration and events.`
            : ''
        }
        confirmButtonText="Withdraw"
        confirmButtonVariant="danger"
      />
    </>
  );
};

export default MeetRegistrationManager;
